import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { futureProductsAPI } from '../services/api';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import FutureProductModal from '../components/FutureProductModal';

const FutureProducts = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const queryClient = useQueryClient();

  const { data: productsData, isLoading, error } = useQuery(
    ['futureProducts'],
    () => futureProductsAPI.getAll(),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        console.error('Future products fetch error:', error);
        toast.error('Failed to load future products');
      }
    }
  );

  const deleteMutation = useMutation(
    (uuid) => futureProductsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('futureProducts');
        toast.success('Future product deleted successfully!');
        setDeleteModalOpen(false);
        setProductToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleDelete = (product) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      deleteMutation.mutate(productToDelete.uuid);
    }
  };

  const handleCreateNew = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
  };

  const products = Array.isArray(productsData?.data?.data) ? productsData.data.data : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load future products</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Future Products</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage upcoming products (coming soon) displayed on the mobile home page
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card-modern p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Products</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{products.length}</div>
        </div>
        <div className="card-modern p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Display Order</div>
          <div className="text-2xl font-bold text-indigo-600">{products.length > 0 ? 'Managed' : 'N/A'}</div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product, index) => (
          <div
            key={product.uuid}
            className="card-modern group hover-lift animate-fade-in-up overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Image */}
            <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt="product"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <span className="text-gray-400">No image</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="space-y-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <div>Order: {product.display_order}</div>
                <div>Added: {new Date(product.created_at).toLocaleDateString()}</div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleEdit(product)}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1 transition-colors flex-1 justify-center"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product)}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 transition-colors flex-1 justify-center"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No future products yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Get started by creating your first future product</p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <FutureProductModal
          product={editingProduct}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Future Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default FutureProducts;
